import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { MiDatePicker, MiDetailFields } from "@miplatform/components";
import {
  BUTTON_LABELS,
  DATEPICKER_VARIANTS,
  DATE_TIME_FORMATS,
  INPUT_TYPES,
  PERMISSIONS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { ICalendarSet, ITransferListItem } from "@miplatform/types";
import { formatDate } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { TransferList } from "@mibuildweb/components";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, Typography } from "@mui/material";
import { ConfigPageLayout } from "../../ConfigPageLayout";
import { ConfigAccordionHeader } from "../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import { IHolidayFormData, defaultHolidayData } from "../ConfigHolidays.types";
import { holidaySchema } from "../schemas/ConfigHoliday.schema";
import { useHolidayDetailsViewModel } from "./useHolidayDetailsViewModel";

export function ConfigHolidayDetails() {
  const { goBack, params } = useRouter();
  const { id: holidayId } = params;

  const { hasPermission } = usePermissions();
  const isReadOnly = !hasPermission(PERMISSIONS.TENANT_HOLIDAY?.UPDATE);

  const form = useForm<IHolidayFormData>({
    mode: "onChange",
    defaultValues: defaultHolidayData,
    resolver: zodResolver(holidaySchema),
  });

  const {
    control,
    formState: { isValid, isDirty },
    getValues,
    setValue,
    reset,
  } = form;

  const {
    getHolidayDetails,
    handleCreateHoliday,
    handleUpdateHoliday,
    orderCalendarSets,
    workOrderCalendarSets,
    isLoading,
  } = useHolidayDetailsViewModel({ reset });

  const watchName = useWatch({ control, name: "name" });
  const startDate = useWatch({ control, name: "startDate" });
  const endDate = useWatch({ control, name: "endDate" });
  const orderCalendarSetIds = useWatch({
    control,
    name: "orderCalendarSetIds",
  });
  const workOrderCalendarSetIds = useWatch({
    control,
    name: "workOrderCalendarSetIds",
  });

  const isCreatingNewHoliday = !holidayId;

  const handleSaveHoliday = async () => {
    const data = getValues();
    if (holidayId) {
      handleUpdateHoliday(data);
    } else {
      handleCreateHoliday(data);
    }
  };

  const canSave = () => {
    if (isLoading) return false;
    if (isReadOnly) return false;
    if (!isValid) return false;
    if (!isDirty) return false;
    return true;
  };

  const holidayActions = isReadOnly
    ? null
    : {
        cancel: {
          label: BUTTON_LABELS.CANCEL,
          onClick: () => {
            reset();
            goBack();
          },
        },
        submit: {
          label: "Save Holiday",
          onClick: handleSaveHoliday,
          disabled: !canSave(),
        },
      };

  const renderPageTitle = () => {
    const action = isCreatingNewHoliday ? "Create" : "Edit";
    const holidayLabel = watchName ? `${watchName}` : "New Holiday";
    return (
      <Typography variant="HD4">
        {action} Holiday:{" "}
        <Typography variant="HD4" color="primary" component="span">
          {holidayLabel}
        </Typography>
      </Typography>
    );
  };

  // --- Date Handlers ---
  const handleStartDateChange = (date: unknown) => {
    setValue(
      "startDate",
      date ? formatDate(date, DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT) : "",
      { shouldValidate: true, shouldDirty: true }
    );
  };

  const handleEndDateChange = (date: unknown) => {
    setValue(
      "endDate",
      date ? formatDate(date, DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT) : "",
      { shouldValidate: true, shouldDirty: true }
    );
  };

  // --- Detail Fields ---
  const detailFields = useMemo(
    () => [
      {
        label: "Holiday Name",
        name: "name",
        width: "100%",
        required: true,
        formControl: control,
      },
      {
        label: "Start Date",
        name: "startDate",
        required: true,
        fieldType: INPUT_TYPES.CUSTOM,
        formControl: control,
        width: "50%",
        customComponent: (
          <MiDatePicker
            value={startDate || null}
            onChange={handleStartDateChange}
            variant={DATEPICKER_VARIANTS.DESKTOP}
            isFullWidth
          />
        ),
      },
      {
        label: "End Date",
        name: "endDate",
        required: true,
        fieldType: INPUT_TYPES.CUSTOM,
        formControl: control,
        width: "50%",
        customComponent: (
          <MiDatePicker
            value={endDate || null}
            onChange={handleEndDateChange}
            variant={DATEPICKER_VARIANTS.DESKTOP}
            isFullWidth
          />
        ),
      },
    ],
    [control, startDate, endDate]
  );

  // --- Transfer List Helpers ---
  const mapCalendarSetsToItems = (
    calendarSets: ICalendarSet[]
  ): ITransferListItem[] =>
    calendarSets.map((cs) => ({
      key: cs.id,
      title: cs.name,
    }));

  const availableOrderCalendars = useMemo(
    () =>
      mapCalendarSetsToItems(
        orderCalendarSets.filter(
          (cs) => !(orderCalendarSetIds || []).includes(cs.id)
        )
      ),
    [orderCalendarSets, orderCalendarSetIds]
  );

  const selectedOrderCalendars: ITransferListItem[] = useMemo(
    () =>
      (orderCalendarSetIds || [])
        .map((id) => {
          const cs = orderCalendarSets.find((c) => c.id === id);
          return cs ? { key: cs.id, title: cs.name } : null;
        })
        .filter(Boolean) as ITransferListItem[],
    [orderCalendarSetIds, orderCalendarSets]
  );

  const availableWorkOrderCalendars = useMemo(
    () =>
      mapCalendarSetsToItems(
        workOrderCalendarSets.filter(
          (cs) => !(workOrderCalendarSetIds || []).includes(cs.id)
        )
      ),
    [workOrderCalendarSets, workOrderCalendarSetIds]
  );

  const selectedWorkOrderCalendars: ITransferListItem[] = useMemo(
    () =>
      (workOrderCalendarSetIds || [])
        .map((id) => {
          const cs = workOrderCalendarSets.find((c) => c.id === id);
          return cs ? { key: cs.id, title: cs.name } : null;
        })
        .filter(Boolean) as ITransferListItem[],
    [workOrderCalendarSetIds, workOrderCalendarSets]
  );

  // --- Transfer List Handlers ---
  const handleAddOrderCalendar = (key: string) => {
    const current = getValues("orderCalendarSetIds") || [];
    if (!current.includes(key)) {
      setValue("orderCalendarSetIds", [...current, key], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const handleRemoveOrderCalendar = (key: string) => {
    const current = getValues("orderCalendarSetIds") || [];
    setValue(
      "orderCalendarSetIds",
      current.filter((id) => id !== key),
      { shouldDirty: true, shouldValidate: true }
    );
  };

  const handleAddWorkOrderCalendar = (key: string) => {
    const current = getValues("workOrderCalendarSetIds") || [];
    if (!current.includes(key)) {
      setValue("workOrderCalendarSetIds", [...current, key], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const handleRemoveWorkOrderCalendar = (key: string) => {
    const current = getValues("workOrderCalendarSetIds") || [];
    setValue(
      "workOrderCalendarSetIds",
      current.filter((id) => id !== key),
      { shouldDirty: true, shouldValidate: true }
    );
  };

  useEffect(() => {
    if (holidayId) {
      getHolidayDetails(holidayId);
    }
  }, [holidayId]);

  return (
    <ConfigPageLayout
      renderTitle={renderPageTitle}
      bottomActions={holidayActions}
      loading={isLoading}
    >
      <Box display="flex" flexDirection="column" gap={2} p={4}>
        <ConfigAccordionHeader title="Holiday Details">
          <MiDetailFields detailFields={detailFields} />
        </ConfigAccordionHeader>

        <ConfigAccordionHeader title="Order Calendars">
          <Box sx={{ minHeight: 300 }}>
            <TransferList
              availableItems={availableOrderCalendars}
              selectedItems={selectedOrderCalendars}
              availableTitle="Available Order Calendar"
              selectedTitle="Selected Order Calendars"
              onAddItem={handleAddOrderCalendar}
              onRemoveItem={handleRemoveOrderCalendar}
              showAvailableItemsSearch
              availableSearchPlaceholder="Search Order Calendar"
              disabled={isReadOnly}
            />
          </Box>
        </ConfigAccordionHeader>

        <ConfigAccordionHeader title="Work Order Calendars">
          <Box sx={{ minHeight: 300 }}>
            <TransferList
              availableItems={availableWorkOrderCalendars}
              selectedItems={selectedWorkOrderCalendars}
              availableTitle="Available Work Order Calendar"
              selectedTitle="Selected Work Order Calendars"
              onAddItem={handleAddWorkOrderCalendar}
              onRemoveItem={handleRemoveWorkOrderCalendar}
              showAvailableItemsSearch
              availableSearchPlaceholder="Search Work Order Calendar"
              disabled={isReadOnly}
            />
          </Box>
        </ConfigAccordionHeader>
      </Box>
    </ConfigPageLayout>
  );
}
